import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, defer, map, Observable, of, switchMap, throwError } from 'rxjs';
import { SugarAccountSearchResult, SugarModule, SugarOpportunitySummary, SugarRecordReference } from 'shared-models';
import { environment } from 'src/environments/environment';
import { SugarAuthService } from '../auth/sugar-auth.service';
import { BaseApiService } from './base-api.service';

/**
 * Letture da SugarCRM passando dal nostro backend (`GET /sugar/:module/:id`): il browser non può
 * chiamare Sugar direttamente, perché Sugar non abilita la CORS.
 *
 * Il backend legge per conto dell'utente, quindi gli serve il token Sugar: viaggia nell'header
 * `X-Sugar-Token`, accanto al nostro bearer che mette `AuthJWTInterceptor`. Si passa da
 * `BaseApiService` perché la rotta è autenticata e il backend pretende `organizationUUID` nella query.
 */
@Injectable({ providedIn: 'root' })
export class SugarApiService {
	private baseApi = inject(BaseApiService);
	private sugarAuth = inject(SugarAuthService);

	/**
	 * Azienda intestataria dell'offerta; `null` se non esiste o non è visibile all'utente.
	 * @param id Id dell'azienda su Sugar.
	 * @returns Un Observable del riferimento all'azienda.
	 */
	getAccount(id: string): Observable<SugarAccountSearchResult | null> {
		return this.findById<SugarAccountSearchResult>('accounts', id);
	}

	searchAccounts(search: string): Observable<SugarAccountSearchResult[]> {
		const endpoint = `${environment.sugarIframe?.sugarUrl}accounts`;
		return this.getWithSugarToken<SugarAccountSearchResult[]>(endpoint, { search: search.trim() });
	}

	/**
	 * Opportunità commerciale da cui nasce l'offerta; `null` se non esiste o non è visibile.
	 * @param id Id dell'opportunità su Sugar.
	 * @returns Un Observable del riferimento all'opportunità.
	 */
	getOpportunity(id: string): Observable<SugarRecordReference | null> {
		return this.findById('opportunities', id);
	}

	/**
	 * Opportunità aperte dell'anagrafica (`GET /sugar/accounts/:id/opportunities`), per il widget di
	 * collegamento. Lista vuota se l'anagrafica non ha opportunità aperte o non è visibile (404).
	 * @param accountId Id dell'anagrafica su Sugar.
	 * @returns Un Observable delle opportunità aperte, ordinate dal backend per data di chiusura.
	 */
	getOpenOpportunities(accountId: string): Observable<SugarOpportunitySummary[]> {
		const endpoint = `${environment.sugarIframe?.sugarUrl}accounts/${encodeURIComponent(accountId)}/opportunities`;
		return this.getWithSugarToken<SugarOpportunitySummary[] | null>(endpoint).pipe(
			catchError((error) => {
				if (error instanceof HttpErrorResponse && error.status === 404) {
					return of(null);
				}
				return throwError(() => error);
			}),
			map((opportunities) => opportunities ?? []),
		);
	}

	/**
	 * Un record per id: l'anagrafica con indirizzo e P.IVA (`SugarAccountSearchResult`), l'opportunità
	 * ridotta a `id` e `name` (`SugarRecordReference`).
	 *
	 * `null` significa **non trovato o non accessibile**: Sugar risponde 404 a entrambi i casi e non li
	 * distingue. Il backend restituisce `404`, che questo servizio converte in `null`.
	 */
	findById<T extends SugarRecordReference>(module: SugarModule, id: string): Observable<T | null> {
		const endpoint = `${environment.sugarIframe?.sugarUrl}${module}/${encodeURIComponent(id)}`;
		return this.getWithSugarToken<T | null>(endpoint).pipe(
			catchError((error) => {
				if (error instanceof HttpErrorResponse && error.status === 404) {
					return of(null);
				}
				return throwError(() => error);
			}),
		);
	}

	private getWithSugarToken<T>(endpoint: string, params: Record<string, string> = {}): Observable<T> {
		return defer(() => this.sugarAuth.getSugarToken()).pipe(
			switchMap((token) => {
				return this.baseApi.get<T>(endpoint, params, { 'X-Sugar-Token': token }).pipe(
					catchError((error) => {
						// 424: Sugar ha rifiutato il token (il backend non usa 401, che sembrerebbe la
						// scadenza del nostro). Si butta, così la prossima lettura rifà l'handshake.
						if (error instanceof HttpErrorResponse && error.status === 424) {
							this.sugarAuth.invalidateSugarToken(token);
						}
						return throwError(() => error);
					}),
				);
			}),
		);
	}
}
